/**
 * DPG Scan Real-Time Visualization Panel
 *
 * Demo Step 3: Shows DPG scanning a connector response containing a SSN.
 * Displays detected PII fields, scan timing, and tokenization in real time.
 *
 * Subscribes to /topic/enforcement/{workflowRunId} for:
 *   - DPG_SCAN_RESULT events
 *   - TOKENIZATION_EVENT events
 */

import React, { useState, useEffect, useRef } from 'react';

const SCAN_STATES = {
  IDLE: 'idle',
  SCANNING: 'scanning',
  DETECTED: 'detected',
  TOKENIZING: 'tokenizing',
  COMPLETE: 'complete',
  BLOCKED: 'blocked',
};

const DATA_TYPE_COLORS = {
  SSN:         { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', label: 'Social Security Number' },
  CREDIT_CARD: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', label: 'Credit Card Number' },
  EMAIL:       { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', label: 'Email Address' },
  PHONE:       { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', label: 'Phone Number' },
  DOB:         { bg: '#faf5ff', border: '#e9d5ff', text: '#6b21a8', label: 'Date of Birth' },
  ADDRESS:     { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', label: 'Street Address' },
  PASSPORT:    { bg: '#fdf2f8', border: '#fbcfe8', text: '#9d174d', label: 'Passport Number' },
  DEFAULT:     { bg: '#f3f4f6', border: '#d1d5db', text: '#374151', label: 'Sensitive Data' },
};

export function DpgScanPanel({ enforcementEvents = [], isExecuting = false }) {
  const [scanState, setScanState] = useState(SCAN_STATES.IDLE);
  const [detections, setDetections] = useState([]);
  const [tokenizations, setTokenizations] = useState([]);
  const [scanTimings, setScanTimings] = useState([]);
  const scrollRef = useRef(null);

  // Process incoming enforcement events
  useEffect(() => {
    if (!enforcementEvents.length) return;

    const latest = enforcementEvents[enforcementEvents.length - 1];

    if (latest.type === 'DPG_SCAN_RESULT') {
      setScanState(latest.blocked ? SCAN_STATES.BLOCKED : SCAN_STATES.DETECTED);
      if (latest.detections) {
        const newDetections = Object.entries(latest.detections).map(([field, info]) => ({
          field,
          dataType: info.dataType || 'DEFAULT',
          value: info.maskedValue || '***REDACTED***',
          originalLength: info.originalLength || 0,
          action: info.action || 'TOKENIZE',
          nodeId: latest.nodeId,
          scanDirection: latest.scanDirection,
          timestamp: latest.timestamp,
        }));
        setDetections(prev => [...prev, ...newDetections]);
      }
      setScanTimings(prev => [...prev, {
        nodeId: latest.nodeId,
        direction: latest.scanDirection,
        timeMs: latest.scanTimeMs,
        detectionsFound: latest.detectionsFound,
        timestamp: latest.timestamp,
      }]);
    }

    if (latest.type === 'TOKENIZATION_EVENT') {
      setScanState(SCAN_STATES.TOKENIZING);
      setTokenizations(prev => [...prev, {
        tokenId: latest.tokenId,
        fieldName: latest.fieldName,
        dataType: latest.dataType,
        encryption: latest.encryptionAlgorithm,
        keyProvider: latest.keyProvider,
        kmsWrapped: latest.kmsWrapped,
        nodeId: latest.nodeId,
        timestamp: latest.timestamp,
      }]);
      // Move to complete after animation
      setTimeout(() => setScanState(SCAN_STATES.COMPLETE), 800);
    }
  }, [enforcementEvents]);

  // Auto-scroll to latest event
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [detections, tokenizations]);

  // Reset when new execution starts
  useEffect(() => {
    if (isExecuting) {
      setScanState(SCAN_STATES.SCANNING);
    } else if (scanState === SCAN_STATES.SCANNING) {
      setScanState(SCAN_STATES.IDLE);
    }
  }, [isExecuting]);

  const getTypeStyle = (dataType) => DATA_TYPE_COLORS[dataType] || DATA_TYPE_COLORS.DEFAULT;

  const totalDetections = detections.length;
  const totalTokenized = tokenizations.length;
  const avgScanTime = scanTimings.length > 0
    ? (scanTimings.reduce((sum, t) => sum + t.timeMs, 0) / scanTimings.length).toFixed(1)
    : 0;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      fontSize: 13,
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
        color: '#fff',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>🛡</span>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Data Protection Guard</span>
        <span style={{
          marginLeft: 'auto',
          fontSize: 11,
          background: scanState === SCAN_STATES.SCANNING ? 'rgba(255,255,255,0.25)' :
                      scanState === SCAN_STATES.BLOCKED ? '#dc2626' :
                      scanState === SCAN_STATES.COMPLETE ? '#16a34a' : 'rgba(255,255,255,0.15)',
          padding: '2px 8px',
          borderRadius: 4,
          animation: scanState === SCAN_STATES.SCANNING ? 'pulse 1.5s infinite' : 'none',
        }}>
          {scanState === SCAN_STATES.IDLE && 'Idle'}
          {scanState === SCAN_STATES.SCANNING && '● Scanning...'}
          {scanState === SCAN_STATES.DETECTED && `⚠ ${totalDetections} Detections`}
          {scanState === SCAN_STATES.TOKENIZING && '🔐 Tokenizing...'}
          {scanState === SCAN_STATES.COMPLETE && `✓ ${totalTokenized} Tokenized`}
          {scanState === SCAN_STATES.BLOCKED && '✕ BLOCKED'}
        </span>
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e5e7eb',
        fontSize: 11,
      }}>
        <div style={{ flex: 1, padding: '6px 12px', borderRight: '1px solid #e5e7eb', textAlign: 'center' }}>
          <div style={{ color: '#6b7280' }}>Detections</div>
          <div style={{ fontWeight: 700, color: totalDetections > 0 ? '#dc2626' : '#374151', fontSize: 16 }}>
            {totalDetections}
          </div>
        </div>
        <div style={{ flex: 1, padding: '6px 12px', borderRight: '1px solid #e5e7eb', textAlign: 'center' }}>
          <div style={{ color: '#6b7280' }}>Tokenized</div>
          <div style={{ fontWeight: 700, color: totalTokenized > 0 ? '#16a34a' : '#374151', fontSize: 16 }}>
            {totalTokenized}
          </div>
        </div>
        <div style={{ flex: 1, padding: '6px 12px', textAlign: 'center' }}>
          <div style={{ color: '#6b7280' }}>Avg Scan</div>
          <div style={{ fontWeight: 700, color: '#374151', fontSize: 16 }}>
            {avgScanTime}ms
          </div>
        </div>
      </div>

      {/* Detections Feed */}
      <div ref={scrollRef} style={{ maxHeight: 320, overflowY: 'auto', padding: 8 }}>
        {detections.length === 0 && !isExecuting && (
          <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
            No PII detections yet. Execute a workflow to see real-time scanning.
          </div>
        )}
        {isExecuting && detections.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
            <div style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: 8 }}>⟳</div>
            <div>Scanning connector responses for sensitive data...</div>
          </div>
        )}

        {detections.map((d, i) => {
          const style = getTypeStyle(d.dataType);
          const isTokenized = tokenizations.some(t => t.fieldName === d.field);

          return (
            <div key={i} style={{
              background: style.bg,
              border: `1px solid ${style.border}`,
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 6,
              transition: 'all 0.3s ease',
              opacity: isTokenized ? 0.7 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{
                  background: style.border,
                  color: style.text,
                  padding: '1px 6px',
                  borderRadius: 3,
                  fontSize: 10,
                  fontWeight: 600,
                }}>
                  {d.dataType}
                </span>
                <span style={{ fontSize: 11, color: '#6b7280' }}>{d.field}</span>
                <span style={{ marginLeft: 'auto', fontSize: 10 }}>
                  {d.scanDirection === 'INBOUND' ? '📥' : '📤'} {d.scanDirection}
                </span>
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: style.text, marginBottom: 4 }}>
                {d.value}
              </div>
              <div style={{ fontSize: 10, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Action: {d.action}</span>
                {isTokenized && (
                  <span style={{
                    background: '#dcfce7',
                    color: '#166534',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 600,
                  }}>
                    🔐 TOKENIZED
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Tokenization Events */}
        {tokenizations.map((t, i) => (
          <div key={`tok-${i}`} style={{
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 14 }}>🔐</span>
              <span style={{ fontWeight: 600, color: '#166534', fontSize: 12 }}>Tokenization Complete</span>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#374151' }}>
              <div>Field: <strong>{t.fieldName}</strong> → Token: <code style={{ background: '#dcfce7', padding: '1px 4px', borderRadius: 2 }}>{t.tokenId}</code></div>
              <div>Encryption: {t.encryption} | Key Provider: {t.keyProvider} | KMS-Wrapped: {t.kmsWrapped ? '✓' : '✕'}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DpgScanPanel;
